import React, { useState, useEffect, useCallback } from 'react';
import { 
  Send, FileText, CheckCircle, XCircle, Clock, AlertCircle,
  Eye, RefreshCw, Settings, ChevronRight, Building2, QrCode,
  ExternalLink, Filter, Search, Key, Shield, FileCode, Loader2,
  ShieldCheck, Download, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import QRCode from 'qrcode';

const ZATCA = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [settings, setSettings] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  // Iter-146 — full ZATCA integration diagnostic
  const [diagnosticBusy,   setDiagnosticBusy]   = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  // Iter-146 — compliance test + production renewal helpers
  const [complianceBusy, setComplianceBusy] = useState(false);
  // Feb-2026 — ZATCA data preservation console (backup/restore certs)
  const [showPreservation, setShowPreservation] = useState(false);
  const [preservationStatus, setPreservationStatus] = useState(null);
  const [preservationBusy, setPreservationBusy] = useState(false);
  const [preservationImport, setPreservationImport] = useState(null);
  const [preservationOverwrite, setPreservationOverwrite] = useState(false);

  const runComplianceTest = async () => {
    try {
      setComplianceBusy(true);
      const r = await axiosInstance.post('/zatca/compliance-test');
      const passed = r?.data?.tests_passed ?? 0;
      const failed = r?.data?.tests_failed ?? 0;
      if (failed === 0 && passed >= 6) {
        toast.success(`اجتاز اختبار الامتثال ${passed}/6 — الآن اضغط زر "ترقية إلى Production CSID"`);
      } else {
        toast.warning(`اختبار الامتثال: ${passed} نجح، ${failed} فشل. راجع السجل.`);
      }
      fetchOnboardingStatus?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل اختبار الامتثال');
    } finally {
      setComplianceBusy(false);
    }
  };

  const promptRenewProduction = async () => {
    const otp = window.prompt(
      'الصق OTP الجديد من بوابة فاتورة (8 أرقام، صالح ساعة):\n\n' +
      'احصل عليه من https://fatoora.zatca.gov.sa → ' +
      'تجديد صلاحية الرمز التعريفي المحدد الحالي'
    );
    if (!otp || otp.trim().length < 6) return;
    try {
      const r = await axiosInstance.post('/zatca/renew-production-csid', { otp: otp.trim() });
      if (r?.data?.success) {
        toast.success('🎉 Production CSID مُفعّل — يمكنك الآن إرسال الفواتير');
        fetchOnboardingStatus?.();
        fetchSettings?.();
      } else {
        toast.error(r?.data?.message || 'فشل التفعيل');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل ترقية الشهادة');
    }
  };

  const runDiagnostic = async () => {
    try {
      setDiagnosticBusy(true);
      const r = await axiosInstance.get('/zatca/diagnostic');
      setDiagnosticResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل تشغيل التشخيص');
    } finally {
      setDiagnosticBusy(false);
    }
  };

  // ── Feb-2026 · ZATCA Preservation Console ──────────────────────
  // Backup + restore console for ZATCA certificates, submissions,
  // and audit trail. Fulfils the user demand: "حتى لو تم التخلي
  // عن البيانات يجب الحفاظ على بيانات ZATCA".
  const fetchPreservationStatus = async () => {
    try {
      setPreservationBusy(true);
      const r = await axiosInstance.get('/zatca/preservation/status');
      setPreservationStatus(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'تعذر جلب حالة الحماية');
    } finally {
      setPreservationBusy(false);
    }
  };

  const openPreservation = () => {
    setShowPreservation(true);
    setPreservationImport(null);
    fetchPreservationStatus();
  };

  const exportZatcaBackup = async () => {
    try {
      setPreservationBusy(true);
      const r = await axiosInstance.get('/zatca/preservation/export');
      const payload = r.data;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `zatca-backup-${payload.company_id || 'tenant'}-${stamp}.json`;
      // Trigger a client-side download so the operator can archive
      // the certificate offline (Article-21 evidence retention).
      const blob = new Blob(
        [JSON.stringify(payload, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = Object.values(payload.summary || {})
        .reduce((s, n) => s + (Number(n) > 0 ? Number(n) : 0), 0);
      toast.success(`تم تنزيل النسخة الاحتياطية (${total} وثيقة)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل التصدير');
    } finally {
      setPreservationBusy(false);
    }
  };

  const onPreservationFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const payload = JSON.parse(ev.target.result);
        if (!payload || typeof payload !== 'object' || !payload.collections) {
          toast.error('الملف غير صالح — لا يحتوي على "collections"');
          return;
        }
        setPreservationImport({ payload, filename: file.name });
        toast.info(`جاهز للاستيراد: ${file.name}`);
      } catch (err) {
        toast.error('تعذر قراءة ملف JSON');
      }
    };
    reader.readAsText(file);
    // reset input so selecting the same file twice still fires
    e.target.value = '';
  };

  const importZatcaBackup = async () => {
    if (!preservationImport?.payload) {
      toast.error('اختر ملف نسخة احتياطية أولاً');
      return;
    }
    if (!window.confirm(
      preservationOverwrite
        ? 'سيتم استبدال بيانات ZATCA الحالية بالكامل. متأكد؟'
        : 'سيتم دمج/تحديث بيانات ZATCA من الملف. متأكد؟',
    )) return;
    try {
      setPreservationBusy(true);
      const r = await axiosInstance.post('/zatca/preservation/import', {
        payload:   preservationImport.payload,
        overwrite: preservationOverwrite,
      });
      const totals = r.data?.imported || {};
      const totalDocs = Object.values(totals).reduce(
        (s, n) => s + (Number(n) || 0), 0);
      toast.success(`تمت استعادة ${totalDocs} وثيقة من النسخة الاحتياطية`);
      setPreservationImport(null);
      await fetchPreservationStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل الاستيراد');
    } finally {
      setPreservationBusy(false);
    }
  };

  // Onboarding form
  const [onboardingForm, setOnboardingForm] = useState({
    otp: '',
    common_name: 'Contractor Books',
    organization_name: '',
    serial_number: '',
    location: '',
    industry: 'Contracting',
    invoice_type: '1100'
  });
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [generatingCSR, setGeneratingCSR] = useState(false);
  const [csrGenerated, setCsrGenerated] = useState(null);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    vat_number: '',
    company_name: '',
    company_name_ar: '',
    street: '',
    building_number: '',
    city: '',
    postal_code: '',
    district: '',
    additional_number: ''
  });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/zatca/invoices');
      setInvoices(response.data.invoices);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('فشل في جلب الفواتير');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/zatca/settings');
      setSettings(response.data);
      if (response.data.settings) {
        setSettingsForm({
          vat_number: response.data.settings.vat_number || '',
          company_name: response.data.settings.company_name || '',
          company_name_ar: response.data.settings.company_name_ar || '',
          street: response.data.settings.street || '',
          building_number: response.data.settings.building_number || '',
          city: response.data.settings.city || '',
          postal_code: response.data.settings.postal_code || '',
          district: response.data.settings.district || '',
          additional_number: response.data.settings.additional_number || ''
        });
        // Update onboarding form with settings
        setOnboardingForm(prev => ({
          ...prev,
          organization_name: response.data.settings.company_name || '',
          location: response.data.settings.city || ''
        }));
      }
    } catch (error) {
      console.error('Failed to fetch ZATCA settings');
    }
  }, []);

  const fetchOnboardingStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/zatca/onboarding-status');
      setOnboardingStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch onboarding status');
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchSettings();
    fetchOnboardingStatus();
  }, [fetchInvoices, fetchSettings, fetchOnboardingStatus]);

  // Generate serial number - ZATCA compliant format (no pipe characters)
  const generateSerialNumber = () => {
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
    // Use hyphen instead of pipe for X.509 PrintableString compatibility
    const sanitizedName = (onboardingForm.common_name || 'EGS').replace(/[^A-Za-z0-9]/g, '');
    return `1-${sanitizedName}-2-1.0-3-${uuid}`;
  };

  // Handle CSR Generation
  const handleGenerateCSR = async () => {
    if (!settingsForm.vat_number) {
      toast.error('يجب إدخال الرقم الضريبي أولاً في الإعدادات');
      setShowOnboarding(false);
      setShowSettings(true);
      return;
    }

    try {
      setGeneratingCSR(true);
      const serialNumber = onboardingForm.serial_number || generateSerialNumber();
      
      const response = await axiosInstance.post('/zatca/generate-csr', {
        common_name: onboardingForm.common_name,
        organization_name: onboardingForm.organization_name || settingsForm.company_name,
        organization_unit: settingsForm.vat_number,
        serial_number: serialNumber,
        location: onboardingForm.location || settingsForm.city,
        industry: onboardingForm.industry,
        invoice_type: onboardingForm.invoice_type
      });

      if (response.data.success) {
        setCsrGenerated(response.data);
        setOnboardingForm(prev => ({ ...prev, serial_number: serialNumber }));
        toast.success('تم إنشاء CSR بنجاح!');
        setOnboardingStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في إنشاء CSR');
    } finally {
      setGeneratingCSR(false);
    }
  };

  // Handle Onboarding
  const handleOnboarding = async () => {
    if (!onboardingForm.otp) {
      toast.error('يجب إدخال رمز التفعيل (OTP)');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axiosInstance.post('/zatca/onboard', {
        otp: onboardingForm.otp,
        common_name: onboardingForm.common_name,
        organization_name: onboardingForm.organization_name || settingsForm.company_name,
        serial_number: onboardingForm.serial_number,
        location: onboardingForm.location || settingsForm.city,
        industry: onboardingForm.industry,
        invoice_type: onboardingForm.invoice_type
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowOnboarding(false);
        fetchOnboardingStatus();
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في التسجيل');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = async (invoice) => {
    try {
      // If clicking the same invoice, close the panel
      if (selectedInvoice?.id === invoice.id) {
        setSelectedInvoice(null);
        setQrCodeUrl('');
        return;
      }
      
      // Set selected invoice
      const response = await axiosInstance.get(`/zatca/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      
      // Generate QR Code
      if (response.data.qr_code_data) {
        const qrUrl = await QRCode.toDataURL(response.data.qr_code_data, {
          width: 150,
          margin: 2
        });
        setQrCodeUrl(qrUrl);
      } else {
        setQrCodeUrl('');
      }
    } catch (error) {
      toast.error('فشل في جلب تفاصيل الفاتورة');
    }
  };

  const handleSubmitToZATCA = async (invoiceId) => {
    if (!settings?.configured) {
      toast.error('يجب إعداد بيانات ZATCA أولاً');
      setShowSettings(true);
      return;
    }

    try {
      setSubmitting(true);
      const response = await axiosInstance.post(`/zatca/submit/${invoiceId}`);
      toast.success(response.data.message);
      fetchInvoices();
      setShowDetails(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في إرسال الفاتورة');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axiosInstance.post('/zatca/settings', settingsForm);
      toast.success('تم حفظ الإعدادات بنجاح');
      fetchSettings();
      setShowSettings(false);
    } catch (error) {
      toast.error('فشل في حفظ الإعدادات');
    }
  };

  const getStatusBadge = (status) => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = (status || 'pending').toLowerCase();
    
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'جاهزة للإرسال' },
      submitted: { color: 'bg-blue-100 text-blue-700', icon: Send, label: 'تم الإرسال' },
      cleared: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'معتمدة' },
      reported: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'مُبلغ عنها' },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'مرفوضة' },
      error: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'خطأ' }
    };
    
    const config = statusConfig[normalizedStatus] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Helper to get invoice type label
  const getInvoiceTypeLabel = (invoice) => {
    const subtype = invoice.invoice_subtype || '0200000';
    if (subtype.startsWith('01')) {
      return { label: 'فاتورة ضريبية (B2B)', color: 'bg-blue-100 text-blue-700' };
    }
    return { label: 'فاتورة مبسطة (B2C)', color: 'bg-purple-100 text-purple-700' };
  };

  // Helper function to get customer name
  const getCustomerName = (invoice) => {
    return invoice.customer?.name || invoice.client_name || invoice.client_name_arabic || '-';
  };

  // Helper function to get invoice date
  const getInvoiceDate = (invoice) => {
    const dateStr = invoice.invoice_date || invoice.created_at || invoice.due_date;
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateStr.split('T')[0] || '-';
    }
  };

  // Helper function to get VAT amount
  const getVatAmount = (invoice) => {
    return invoice.vat_amount || invoice.tax_amount || 0;
  };

  const filteredInvoices = invoices.filter(inv => {
    const status = (inv.zatca_status || '').toLowerCase();
    const matchesFilter = filter === 'all' || 
      status === filter.toLowerCase() || 
      (!inv.zatca_status && filter === 'pending');
    const customerName = getCustomerName(inv);
    const matchesSearch = !searchTerm || 
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="zatca-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-green-600" />
            هيئة الزكاة والضريبة والجمارك
          </h1>
          <p className="text-gray-500 mt-1">
            ZATCA E-Invoicing - الفوترة الإلكترونية
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowOnboarding(true)}
            className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            data-testid="zatca-onboard-btn"
          >
            <Key className="w-4 h-4" /> تسجيل الجهاز (OTP)
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="zatca-settings-btn"
          >
            <Settings className="w-4 h-4" /> الإعدادات
          </button>
          <button
            onClick={runDiagnostic}
            className="btn-secondary flex items-center gap-2 bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
            data-testid="zatca-diagnostic-btn"
            disabled={diagnosticBusy}
          >
            {diagnosticBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            تشخيص شامل
          </button>
          <button
            onClick={openPreservation}
            className="btn-secondary flex items-center gap-2 bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
            data-testid="zatca-preservation-btn"
            title="نسخة احتياطية من شهادة ZATCA"
          >
            <ShieldCheck className="w-4 h-4" /> نسخ احتياطي
          </button>
          <button
            onClick={fetchInvoices}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> تحديث
          </button>
        </div>
      </div>

      {/* Diagnostic Result Modal */}
      {diagnosticResult && (
        <div
          data-testid="zatca-diagnostic-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setDiagnosticResult(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span className="font-bold">تشخيص ربط ZATCA الشامل</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  diagnosticResult.overall?.ready_to_submit
                    ? 'bg-emerald-500 text-white'
                    : 'bg-amber-400 text-amber-900'
                }`}>
                  {diagnosticResult.overall?.ready_to_submit ? '✓ جاهز للإرسال' : '⚠ يحتاج إصلاح'}
                </span>
              </div>
              <button
                onClick={() => setDiagnosticResult(null)}
                className="hover:bg-white/20 rounded p-1"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3 text-sm">
              <DiagnosticRow label="البيئة / Environment" value={diagnosticResult.environment} ok />
              <DiagnosticRow label="ZATCA Gateway" value={diagnosticResult.api_url} ok />

              <h4 className="font-bold text-gray-700 mt-3">الإعدادات</h4>
              <DiagnosticRow label="الرقم الضريبي" value={diagnosticResult.checks?.settings?.vat_number} ok={!!diagnosticResult.checks?.settings?.vat_number} />
              <DiagnosticRow label="اسم الشركة (EN)" value={diagnosticResult.checks?.settings?.company_name_en} ok={!!diagnosticResult.checks?.settings?.company_name_en} />
              <DiagnosticRow label="اسم الشركة (AR)" value={diagnosticResult.checks?.settings?.company_name_ar} ok={!!diagnosticResult.checks?.settings?.company_name_ar} />
              <DiagnosticRow label="المدينة" value={diagnosticResult.checks?.settings?.city} ok={!!diagnosticResult.checks?.settings?.city} />
              <DiagnosticRow label="رقم المبنى" value={diagnosticResult.checks?.settings?.building_number} ok={!!diagnosticResult.checks?.settings?.building_number} />
              <DiagnosticRow label="الحي" value={diagnosticResult.checks?.settings?.district} ok={!!diagnosticResult.checks?.settings?.district} />
              <DiagnosticRow label="الرمز البريدي" value={diagnosticResult.checks?.settings?.postal_code} ok={!!diagnosticResult.checks?.settings?.postal_code} />
              <DiagnosticRow label="الرقم الإضافي" value={diagnosticResult.checks?.settings?.additional_number} ok={!!diagnosticResult.checks?.settings?.additional_number} />

              <h4 className="font-bold text-gray-700 mt-3">الشهادات (Certificates)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <CertStatusCard
                  title="CSR"
                  subtitle="طلب توقيع الشهادة"
                  present={!!diagnosticResult.checks?.csr?.ok}
                  detail={diagnosticResult.checks?.csr?.ok ? `${diagnosticResult.checks.csr.length} حرف` : 'غير موجود'}
                  testId="cert-card-csr"
                />
                <CertStatusCard
                  title="Compliance CSID"
                  subtitle="شهادة الامتثال"
                  present={!!diagnosticResult.checks?.compliance_csid?.present}
                  detail={diagnosticResult.checks?.compliance_csid?.present ? 'مُفعّلة' : 'مفقودة'}
                  cert={diagnosticResult.checks?.compliance_csid?.cert}
                  testId="cert-card-compliance"
                />
                <CertStatusCard
                  title="Production CSID"
                  subtitle="شهادة الإنتاج"
                  present={!!diagnosticResult.checks?.production_csid?.present}
                  detail={diagnosticResult.checks?.production_csid?.present ? 'مُفعّلة' : 'مفقودة'}
                  cert={diagnosticResult.checks?.production_csid?.cert}
                  testId="cert-card-production"
                />
              </div>
              <DiagnosticRow label="Private Key" value={diagnosticResult.checks?.credentials?.private_key_present ? 'موجود' : 'مفقود'} ok={diagnosticResult.checks?.credentials?.private_key_present} />

              <h4 className="font-bold text-gray-700 mt-3">الاتصال</h4>
              <DiagnosticRow
                label="ZATCA Gateway"
                value={`HTTP ${diagnosticResult.checks?.connectivity?.http_status || '—'}`}
                ok={diagnosticResult.checks?.connectivity?.ok}
              />

              <h4 className="font-bold text-gray-700 mt-3">آخر محاولة إرسال</h4>
              {diagnosticResult.checks?.last_submission?.none ? (
                <p className="text-xs text-gray-500">لا توجد محاولات إرسال سابقة</p>
              ) : (
                <pre className="text-[10px] bg-gray-50 rounded p-2 max-h-32 overflow-auto font-mono direction-ltr" dir="ltr">
                  {JSON.stringify(diagnosticResult.checks?.last_submission, null, 2)}
                </pre>
              )}

              {diagnosticResult.overall?.failures?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-3">
                  <div className="font-bold text-amber-800 text-xs mb-1">إصلاحات مطلوبة:</div>
                  <ul className="text-xs text-amber-900 list-disc pr-5 space-y-0.5">
                    {diagnosticResult.overall.failures.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feb-2026 · ZATCA Data Preservation Console
          Backup + restore the taxpayer's ZATCA state (certificate,
          submissions, audit trail).  Powered by
          /api/zatca/preservation/{status,export,import}. */}
      {showPreservation && (
        <div
          data-testid="zatca-preservation-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowPreservation(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <span className="font-bold">نسخة احتياطية لشهادات ZATCA</span>
              </div>
              <button
                onClick={() => setShowPreservation(false)}
                className="hover:bg-white/20 rounded p-1"
                data-testid="zatca-preservation-close"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-5 space-y-4 text-sm">
              {/* Status panel */}
              <div className="bg-slate-50 border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-slate-700">
                    حالة بيانات ZATCA الحالية
                  </div>
                  <button
                    onClick={fetchPreservationStatus}
                    disabled={preservationBusy}
                    className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900"
                    data-testid="zatca-preservation-refresh"
                  >
                    {preservationBusy
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <RefreshCw className="w-3 h-3" />}
                    تحديث
                  </button>
                </div>
                {preservationStatus ? (
                  <div className="space-y-1">
                    {Object.entries(preservationStatus.collections || {}).map(([col, count]) => {
                      const critical = col === 'zatca_settings';
                      const ok = Number(count) > 0;
                      return (
                        <div
                          key={col}
                          className="flex items-center justify-between text-xs"
                          data-testid={`zatca-preservation-row-${col}`}
                        >
                          <span className="font-mono text-slate-600" dir="ltr">{col}</span>
                          <span className={`px-2 py-0.5 rounded-full font-bold ${
                            ok
                              ? 'bg-emerald-100 text-emerald-700'
                              : critical
                                ? 'bg-red-100 text-red-700'
                                : 'bg-slate-100 text-slate-500'
                          }`}>
                            {count === -1 ? 'خطأ' : `${count} وثيقة`}
                          </span>
                        </div>
                      );
                    })}
                    {!preservationStatus.zatca_settings_present && (
                      <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                        ⚠️ لا توجد إعدادات ZATCA محفوظة على هذه المنصة. إذا كانت لديك نسخة احتياطية، استعدها الآن من قسم &quot;استيراد&quot;.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">جاري التحميل...</div>
                )}
              </div>

              {/* Export */}
              <div className="border rounded-lg p-3">
                <div className="font-bold text-slate-700 flex items-center gap-2 mb-2">
                  <Download className="w-4 h-4" /> تصدير نسخة احتياطية
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  حمّل ملف JSON يحتوي على شهادة ZATCA وبيانات الاعتماد وسجل الفواتير المرسلة. احتفظ به في مكان آمن — يمكنك استعادته لاحقاً في حال فقد بيانات المنصة.
                </p>
                <button
                  onClick={exportZatcaBackup}
                  disabled={preservationBusy}
                  className="btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm"
                  data-testid="zatca-preservation-export-btn"
                >
                  {preservationBusy
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Download className="w-4 h-4" />}
                  تنزيل ملف النسخة الاحتياطية (.json)
                </button>
              </div>

              {/* Import */}
              <div className="border rounded-lg p-3">
                <div className="font-bold text-slate-700 flex items-center gap-2 mb-2">
                  <Upload className="w-4 h-4" /> استعادة من نسخة احتياطية
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  اختر ملف JSON تم تصديره سابقاً لاستعادة الشهادة وبيانات ZATCA. يمكن الاستعادة إضافياً (دمج) أو الاستبدال الكامل.
                </p>
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={onPreservationFileSelected}
                  className="block w-full text-xs text-slate-600 file:me-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 mb-2"
                  data-testid="zatca-preservation-file-input"
                />
                {preservationImport && (
                  <div className="text-xs text-slate-700 mb-2 bg-slate-50 border rounded p-2">
                    <div>
                      الملف المختار:{' '}
                      <span className="font-mono" dir="ltr">
                        {preservationImport.filename}
                      </span>
                    </div>
                    <div>
                      الشركة الأصلية:{' '}
                      <span className="font-mono" dir="ltr">
                        {preservationImport.payload?.company_id || '—'}
                      </span>
                    </div>
                    <div>
                      المجموعات:{' '}
                      {Object.entries(preservationImport.payload?.summary || {})
                        .map(([k, v]) => `${k}=${v}`)
                        .join('، ') || '—'}
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs text-slate-700 mb-3">
                  <input
                    type="checkbox"
                    checked={preservationOverwrite}
                    onChange={(e) => setPreservationOverwrite(e.target.checked)}
                    data-testid="zatca-preservation-overwrite"
                  />
                  <span>
                    استبدال كامل (مسح البيانات الحالية أولاً). اترك بدون تحديد للدمج الآمن.
                  </span>
                </label>
                <button
                  onClick={importZatcaBackup}
                  disabled={preservationBusy || !preservationImport}
                  className="btn-primary flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm"
                  data-testid="zatca-preservation-import-btn"
                >
                  {preservationBusy
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Upload className="w-4 h-4" />}
                  استعادة الآن
                </button>
              </div>

              <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 leading-relaxed">
                🛡️ بيانات ZATCA محمية تلقائياً على مستوى النظام: أي عملية إعادة تعيين للبيانات أو استيراد من مصدر آخر لن تمس شهاداتك أو سجل الفواتير المرسلة.
              </div>
            </div>
          </div>
        </div>
      )}
      {onboardingStatus && (() => {
        const isProd  = !!onboardingStatus.steps?.production_ready;
        const isCompl = !!onboardingStatus.steps?.compliance_completed && !isProd;
        const isNone  = !onboardingStatus.credentials_configured;
        const cls = isProd ? 'bg-green-50 border-green-200'
                  : isCompl ? 'bg-amber-50 border-amber-300'
                  : 'bg-amber-50 border-amber-200';
        const Icon = isProd ? CheckCircle : isCompl ? AlertCircle : AlertCircle;
        const iconCls = isProd ? 'text-green-600' : 'text-amber-600';
        const textCls = isProd ? 'text-green-800' : 'text-amber-800';
        const title = isProd
          ? '✅ جاهز للإرسال — Production CSID مُفعّل'
          : isCompl
          ? '⚠️ في وضع الامتثال فقط — لا يمكن إرسال فواتير حقيقية بعد'
          : '⏳ لم يتم التسجيل بعد — اضغط على "تسجيل الجهاز"';
        const subtitle = isCompl
          ? 'يجب تنفيذ اختبار الامتثال ثم ترقية الشهادة إلى Production CSID'
          : `البيئة: ${onboardingStatus.environment === 'sandbox' ? 'بيئة الاختبار' : 'الإنتاج'}`;
        return (
          <div className={`rounded-xl shadow-sm p-4 border ${cls}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Icon className={`w-6 h-6 ${iconCls}`} />
                <div>
                  <p className={`font-medium ${textCls}`}>{title}</p>
                  <p className="text-sm text-gray-600">{subtitle}</p>
                </div>
              </div>
              <div className="flex gap-2 text-sm">
                <span className={`px-2 py-1 rounded ${onboardingStatus.steps?.settings_configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  الإعدادات {onboardingStatus.steps?.settings_configured ? '✓' : '○'}
                </span>
                <span className={`px-2 py-1 rounded ${onboardingStatus.steps?.csr_generated ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  CSR {onboardingStatus.steps?.csr_generated ? '✓' : '○'}
                </span>
                <span className={`px-2 py-1 rounded ${onboardingStatus.steps?.compliance_completed || isProd ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  Compliance {onboardingStatus.steps?.compliance_completed || isProd ? '✓' : '○'}
                </span>
                <span className={`px-2 py-1 rounded ${isProd ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  Production {isProd ? '✓' : '○'}
                </span>
              </div>
            </div>
            {/* Compliance-only state: prompt next steps inline */}
            {isCompl && (
              <div className="mt-3 pt-3 border-t border-amber-300 flex flex-wrap items-center gap-2">
                <span className="text-xs text-amber-900 font-medium">
                  الخطوات المتبقية:
                </span>
                <button
                  data-testid="run-compliance-test-btn"
                  onClick={runComplianceTest}
                  disabled={complianceBusy}
                  className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white rounded-full inline-flex items-center gap-1"
                >
                  {complianceBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  ١. تنفيذ اختبار الامتثال (6 فواتير)
                </button>
                <button
                  data-testid="renew-production-csid-btn"
                  onClick={promptRenewProduction}
                  className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                >
                  ٢. ترقية إلى Production CSID (OTP)
                </button>
              </div>
            )}
            {isNone && (
              <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-900">
                ابدأ بحفظ الإعدادات ثم اضغط &quot;تسجيل الجهاز&quot; لتوليد CSR وإدخال OTP.
              </div>
            )}
          </div>
        );
      })()}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">إجمالي الفواتير</p>
          <p className="text-2xl font-bold text-gray-800">{stats.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">جاهزة للإرسال</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">تم الإرسال</p>
          <p className="text-2xl font-bold text-blue-600">{stats.submitted || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">مُبلغ عنها (B2C)</p>
          <p className="text-2xl font-bold text-green-600">{stats.reported || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">معتمدة (B2B)</p>
          <p className="text-2xl font-bold text-green-600">{stats.cleared || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <p className="text-sm text-gray-500">مرفوضة</p>
          <p className="text-2xl font-bold text-red-600">{stats.rejected || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="بحث برقم الفاتورة أو اسم العميل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none w-64"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="all">جميع الفواتير</option>
            <option value="pending">جاهزة للإرسال</option>
            <option value="reported">مُبلغ عنها (B2C)</option>
            <option value="cleared">معتمدة (B2B)</option>
            <option value="rejected">مرفوضة</option>
          </select>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex gap-6">
        {/* Invoices Table */}
        <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 ${selectedInvoice ? 'w-1/2' : 'w-full'}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">رقم الفاتورة</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">العميل</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">التاريخ</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">النوع</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">الإجمالي</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">حالة ZATCA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((invoice) => {
                    const invoiceType = getInvoiceTypeLabel(invoice);
                    const isSelected = selectedInvoice?.id === invoice.id;
                    return (
                    <tr 
                      key={invoice.id} 
                      onClick={() => handleViewDetails(invoice)}
                      className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border-r-4 border-blue-500' : 'hover:bg-gray-50'}`}
                      data-testid={`zatca-invoice-${invoice.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-800">{invoice.invoice_number}</td>
                      <td className="px-4 py-3 text-gray-600">{getCustomerName(invoice)}</td>
                      <td className="px-4 py-3 text-gray-600">{getInvoiceDate(invoice)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${invoiceType.color}`}>
                          {invoiceType.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">{formatCurrency(invoice.total)}</td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(invoice.zatca_status || 'pending')}
                      </td>
                    </tr>
                  );})
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد فواتير</p>
                      <p className="text-sm mt-1">الفواتير المعتمدة والمرسلة ستظهر هنا</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice Details Panel */}
        {selectedInvoice && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 sticky top-4 max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-l from-green-50 to-white flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedInvoice.invoice_number}</h3>
                <p className="text-sm text-gray-500">تفاصيل الفاتورة</p>
              </div>
              <button 
                onClick={() => setSelectedInvoice(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* ZATCA Status */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-gray-700">حالة ZATCA:</span>
                    {getStatusBadge(selectedInvoice.zatca_status)}
                  </div>
                </div>
              </div>

              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">رقم الفاتورة</p>
                  <p className="font-medium">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">التاريخ</p>
                  <p className="font-medium">{getInvoiceDate(selectedInvoice)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">العميل</p>
                  <p className="font-medium">{getCustomerName(selectedInvoice)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">الرقم الضريبي للعميل</p>
                  <p className="font-medium">{selectedInvoice.customer?.vat_number || selectedInvoice.customer_vat_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">نوع الفاتورة</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getInvoiceTypeLabel(selectedInvoice).color}`}>
                    {getInvoiceTypeLabel(selectedInvoice).label}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">حالة الفاتورة</p>
                  <p className="font-medium capitalize">{selectedInvoice.status}</p>
                </div>
              </div>

              {/* Items */}
              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium mb-3 text-gray-700">البنود</h4>
                  <div className="space-y-2">
                    {selectedInvoice.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-white rounded-lg p-2">
                        <span className="text-gray-700">{item.description}</span>
                        <span className="font-medium">{formatCurrency(item.total || item.unit_price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Amounts */}
              <div className="bg-gradient-to-l from-green-50 to-gray-50 rounded-xl p-4">
                <h4 className="font-medium mb-3 text-gray-700">المبالغ</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">المبلغ قبل الضريبة</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">ضريبة القيمة المضافة ({selectedInvoice.vat_rate || selectedInvoice.tax_rate || 15}%)</span>
                    <span className="font-medium text-orange-600">{formatCurrency(getVatAmount(selectedInvoice))}</span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between">
                    <span className="font-bold text-gray-800">الإجمالي</span>
                    <span className="font-bold text-green-600 text-lg">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>
              </div>

              {/* QR Code if available */}
              {qrCodeUrl && (
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">رمز QR للفاتورة</p>
                  <img src={qrCodeUrl} alt="ZATCA QR Code" className="mx-auto w-32 h-32" />
                </div>
              )}

              {/* Push Up Button */}
              {(!selectedInvoice.zatca_status || 
                !['reported', 'cleared'].includes((selectedInvoice.zatca_status || '').toLowerCase())) && (
                <button
                  onClick={() => handleSubmitToZATCA(selectedInvoice.id)}
                  disabled={submitting}
                  className="w-full py-4 bg-gradient-to-l from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                  data-testid="push-up-zatca-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      جاري الإرسال إلى ZATCA...
                    </>
                  ) : (
                    <>
                      <Send className="w-6 h-6" />
                      Push Up - إرسال إلى هيئة الزكاة
                    </>
                  )}
                </button>
              )}

              {/* Already Submitted Message */}
              {selectedInvoice.zatca_status && 
                ['reported', 'cleared'].includes((selectedInvoice.zatca_status || '').toLowerCase()) && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="font-medium text-green-800">تم إرسال الفاتورة بنجاح</p>
                  <p className="text-sm text-green-600">
                    {(selectedInvoice.zatca_status || '').toLowerCase() === 'reported' 
                      ? 'تم الإبلاغ عن الفاتورة (B2C)' 
                      : 'تم اعتماد الفاتورة (B2B)'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold">إعدادات ZATCA</h2>
              <button onClick={() => setShowSettings(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الرقم الضريبي (VAT Number) *
                  </label>
                  <input
                    type="text"
                    value={settingsForm.vat_number}
                    onChange={(e) => setSettingsForm({...settingsForm, vat_number: e.target.value})}
                    placeholder="300000000000000"
                    className="w-full border rounded-lg px-3 py-2"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم الشركة (English) *
                  </label>
                  <input
                    type="text"
                    value={settingsForm.company_name}
                    onChange={(e) => setSettingsForm({...settingsForm, company_name: e.target.value})}
                    placeholder="Company Name"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم الشركة (بالعربي) *
                  </label>
                  <input
                    type="text"
                    value={settingsForm.company_name_ar}
                    onChange={(e) => setSettingsForm({...settingsForm, company_name_ar: e.target.value})}
                    placeholder="اسم الشركة"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الشارع *</label>
                  <input
                    type="text"
                    value={settingsForm.street}
                    onChange={(e) => setSettingsForm({...settingsForm, street: e.target.value})}
                    placeholder="شارع الملك فهد"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رقم المبنى *</label>
                  <input
                    type="text"
                    value={settingsForm.building_number}
                    onChange={(e) => setSettingsForm({...settingsForm, building_number: e.target.value})}
                    placeholder="1234"
                    className="w-full border rounded-lg px-3 py-2"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الحي *</label>
                  <input
                    type="text"
                    value={settingsForm.district}
                    onChange={(e) => setSettingsForm({...settingsForm, district: e.target.value})}
                    placeholder="العليا"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدينة *</label>
                  <input
                    type="text"
                    value={settingsForm.city}
                    onChange={(e) => setSettingsForm({...settingsForm, city: e.target.value})}
                    placeholder="الرياض"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الرمز البريدي *</label>
                  <input
                    type="text"
                    value={settingsForm.postal_code}
                    onChange={(e) => setSettingsForm({...settingsForm, postal_code: e.target.value})}
                    placeholder="12345"
                    className="w-full border rounded-lg px-3 py-2"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الرقم الإضافي</label>
                  <input
                    type="text"
                    value={settingsForm.additional_number}
                    onChange={(e) => setSettingsForm({...settingsForm, additional_number: e.target.value})}
                    placeholder="6789"
                    className="w-full border rounded-lg px-3 py-2"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Registration Guide Link */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  خطوات التسجيل في ZATCA
                </h4>
                <ol className="mt-2 text-sm text-blue-700 list-decimal list-inside space-y-1">
                  <li>سجل في بوابة فاتورة: <a href="https://fatoora.zatca.gov.sa/" target="_blank" rel="noopener noreferrer" className="underline">fatoora.zatca.gov.sa</a></li>
                  <li>أنشئ CSR (طلب توقيع الشهادة)</li>
                  <li>احصل على OTP من البوابة</li>
                  <li>فعّل الشهادة واحصل على CSID</li>
                  <li>اختبر الامتثال في بيئة الاختبار</li>
                  <li>احصل على شهادة الإنتاج</li>
                </ol>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowSettings(false)}
                  className="btn-secondary flex-1"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="btn-primary flex-1"
                >
                  حفظ الإعدادات
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Key className="w-6 h-6 text-green-600" />
                  تسجيل الجهاز في ZATCA
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  الخطوة {onboardingStep} من 2
                </p>
              </div>
              <button onClick={() => { setShowOnboarding(false); setOnboardingStep(1); }} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Step Indicator */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className={`flex items-center gap-2 ${onboardingStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${onboardingStep >= 1 ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {onboardingStep > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
                  </div>
                  <span className="text-sm font-medium">إنشاء CSR</span>
                </div>
                <div className="w-16 h-0.5 bg-gray-200"></div>
                <div className={`flex items-center gap-2 ${onboardingStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${onboardingStep >= 2 ? 'bg-green-100' : 'bg-gray-100'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium">إدخال OTP</span>
                </div>
              </div>

              {onboardingStep === 1 && (
                <>
                  <div className="bg-blue-50 rounded-xl p-4 mb-4">
                    <h4 className="font-medium text-blue-800 mb-2">📝 ما هو CSR؟</h4>
                    <p className="text-sm text-blue-700">
                      CSR (طلب توقيع الشهادة) هو ملف تشفير يحتوي على بيانات شركتك. 
                      سيتم إرساله إلى ZATCA للحصول على شهادة رقمية لتوقيع الفواتير.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        اسم النظام (Common Name) *
                      </label>
                      <input
                        type="text"
                        value={onboardingForm.common_name}
                        onChange={(e) => setOnboardingForm({...onboardingForm, common_name: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="Contractor Books"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        اسم الشركة (Organization) *
                      </label>
                      <input
                        type="text"
                        value={onboardingForm.organization_name || settingsForm.company_name}
                        onChange={(e) => setOnboardingForm({...onboardingForm, organization_name: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="Company Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        المدينة *
                      </label>
                      <input
                        type="text"
                        value={onboardingForm.location || settingsForm.city}
                        onChange={(e) => setOnboardingForm({...onboardingForm, location: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="الرياض"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع الفاتورة *
                      </label>
                      <select
                        value={onboardingForm.invoice_type}
                        onChange={(e) => setOnboardingForm({...onboardingForm, invoice_type: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="1100">فاتورة ضريبية (B2B)</option>
                        <option value="0100">فاتورة مبسطة (B2C)</option>
                        <option value="1000">كلاهما</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الرقم التسلسلي للجهاز
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={onboardingForm.serial_number}
                          onChange={(e) => setOnboardingForm({...onboardingForm, serial_number: e.target.value})}
                          className="flex-1 border rounded-lg px-3 py-2 text-sm"
                          placeholder="سيتم توليده تلقائياً"
                          dir="ltr"
                        />
                        <button
                          onClick={() => setOnboardingForm({...onboardingForm, serial_number: generateSerialNumber()})}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                        >
                          توليد
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowOnboarding(false)}
                      className="btn-secondary flex-1"
                    >
                      إلغاء
                    </button>
                    <button
                      onClick={handleGenerateCSR}
                      disabled={generatingCSR}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
                    >
                      {generatingCSR ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FileCode className="w-4 h-4" />
                      )}
                      {generatingCSR ? 'جاري الإنشاء...' : 'إنشاء CSR'}
                    </button>
                  </div>
                </>
              )}

              {onboardingStep === 2 && (
                <>
                  <div className="bg-green-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-green-800">✅ تم إنشاء CSR بنجاح!</h4>
                    </div>
                    <p className="text-sm text-green-700">
                      الآن أدخل رمز التفعيل (OTP) الذي حصلت عليه من بوابة فاتورة
                    </p>
                  </div>

                  <div className="bg-amber-50 rounded-xl p-4 mb-4">
                    <h4 className="font-medium text-amber-800 mb-2">⚠️ تذكير مهم:</h4>
                    <ul className="text-sm text-amber-700 list-disc list-inside">
                      <li>رمز OTP صالح لمدة <strong>ساعة واحدة فقط</strong></li>
                      <li>تأكد من نسخ الرمز بشكل صحيح من بوابة فاتورة</li>
                      <li>احصل على رمز جديد من: <a href="https://fatoora.zatca.gov.sa/" target="_blank" rel="noopener noreferrer" className="underline text-amber-800 font-medium">fatoora.zatca.gov.sa</a></li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        رمز التفعيل (OTP) *
                      </label>
                      <input
                        type="text"
                        value={onboardingForm.otp}
                        onChange={(e) => setOnboardingForm({...onboardingForm, otp: e.target.value})}
                        className="w-full border-2 border-green-300 rounded-lg px-4 py-3 text-2xl text-center font-mono tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                        dir="ltr"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setOnboardingStep(1)}
                      className="btn-secondary flex-1"
                    >
                      رجوع
                    </button>
                    <button
                      onClick={handleOnboarding}
                      disabled={submitting || !onboardingForm.otp}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Shield className="w-4 h-4" />
                      )}
                      {submitting ? 'جاري التسجيل...' : 'تسجيل وتفعيل'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZATCA;

// ─── Diagnostic display helpers (Iter-146) ────────────────────────
const DiagnosticRow = ({ label, value, ok }) => (
  <div className="flex items-center justify-between gap-2 text-xs py-1 border-b border-gray-100">
    <span className="text-gray-600">{label}</span>
    <span className="flex items-center gap-1.5">
      <span className={ok ? "text-emerald-700" : "text-red-700"}>
        {ok ? "✓" : "✗"}
      </span>
      <span className="font-mono text-gray-800 break-all max-w-[260px] text-right" dir="ltr">
        {value || "—"}
      </span>
    </span>
  </div>
);

const CertBlock = ({ cert }) => {
  if (!cert?.ok) {
    return (
      <div className="text-[11px] text-red-700 bg-red-50 rounded p-2 ml-3">
        تعذر فك ترميز الشهادة: {cert?.error || "unknown"}
      </div>
    );
  }
  const expired = cert.is_expired;
  const soon = !expired && cert.days_until_expiry < 60;
  return (
    <div className={`text-[11px] rounded p-2 ml-3 mr-3 border ${expired ? "bg-red-50 border-red-200" : soon ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
        <span className="text-gray-500">Subject:</span>
        <span className="font-mono break-all" dir="ltr">{cert.subject}</span>
        <span className="text-gray-500">Issuer:</span>
        <span className="font-mono break-all" dir="ltr">{cert.issuer}</span>
        <span className="text-gray-500">Serial:</span>
        <span className="font-mono" dir="ltr">{cert.serial_number}</span>
        <span className="text-gray-500">Issued:</span>
        <span className="font-mono" dir="ltr">{cert.not_valid_before?.slice(0, 10)}</span>
        <span className="text-gray-500">Expires:</span>
        <span className="font-mono" dir="ltr">{cert.not_valid_after?.slice(0, 10)}</span>
        <span className="text-gray-500">Days left:</span>
        <span className={`font-bold ${expired ? "text-red-700" : soon ? "text-amber-700" : "text-emerald-700"}`}>
          {expired ? "منتهية الصلاحية" : `${cert.days_until_expiry} يوماً`}
        </span>
      </div>
    </div>
  );
};

// Colored status card for ZATCA certificates (CSR / Compliance CSID / Production CSID).
// User reported that previously-colored cards were displaying white — this restores
// the explicit green/amber/red visual feedback based on presence + expiry.
const CertStatusCard = ({ title, subtitle, present, detail, cert, testId }) => {
  // Determine tone:
  //  • not present                       → red
  //  • present + cert.is_expired         → red
  //  • present + days_until_expiry < 60  → amber (expiring soon)
  //  • present (no cert detail OR fine)  → emerald (green)
  let tone = 'gray';
  if (!present) {
    tone = 'red';
  } else if (cert && cert.ok === false) {
    tone = 'amber'; // present but couldn't decode → warn, not green
  } else if (cert?.is_expired) {
    tone = 'red';
  } else if (cert && typeof cert.days_until_expiry === 'number' && cert.days_until_expiry < 60) {
    tone = 'amber';
  } else if (present) {
    tone = 'emerald';
  }

  const palette = {
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-800',
    amber:   'bg-amber-50 border-amber-300 text-amber-800',
    red:     'bg-red-50 border-red-300 text-red-800',
    gray:    'bg-gray-50 border-gray-200 text-gray-600',
  }[tone];

  const Icon = tone === 'emerald' ? CheckCircle : tone === 'amber' ? AlertCircle : XCircle;

  return (
    <div
      data-testid={testId}
      className={`rounded-xl border-2 p-3 flex flex-col gap-1 ${palette}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-4 h-4" />
          <span className="font-bold text-xs">{title}</span>
        </div>
        <span className="text-[10px] opacity-75">{subtitle}</span>
      </div>
      <div className="text-xs font-semibold">{detail}</div>
      {cert?.ok && (
        <div className="text-[10px] mt-1 pt-1 border-t border-current/20 space-y-0.5">
          <div className="flex justify-between gap-2">
            <span className="opacity-70">انتهاء:</span>
            <span className="font-mono" dir="ltr">{cert.not_valid_after?.slice(0, 10)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="opacity-70">الأيام المتبقية:</span>
            <span className="font-bold" dir="ltr">
              {cert.is_expired ? 'منتهية' : `${cert.days_until_expiry} يوم`}
            </span>
          </div>
        </div>
      )}
      {cert && cert.ok === false && (
        <div className="text-[10px] mt-1 opacity-80">
          تعذر فك ترميز الشهادة: {cert.error || 'unknown'}
        </div>
      )}
    </div>
  );
};
